import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

type BreakingNewsItem = {
  id: string;
  title: string;
  slug: string;
};

const DEFAULT_TICKER_SPEED_SECONDS = 75;
const MIN_TICKER_SPEED_SECONDS = 20;
const MAX_TICKER_SPEED_SECONDS = 180;

function clampTickerSpeed(value: unknown) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return DEFAULT_TICKER_SPEED_SECONDS;
  return Math.min(MAX_TICKER_SPEED_SECONDS, Math.max(MIN_TICKER_SPEED_SECONDS, parsedValue));
}

const publishedNowFilter = () => {
  const now = new Date().toISOString();
  return `published_at.is.null,published_at.lte.${now}`;
};

export default function BreakingNewsTicker() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<BreakingNewsItem[]>([]);
  const [speedSeconds, setSpeedSeconds] = useState(DEFAULT_TICKER_SPEED_SECONDS);

  const shouldHide = location.pathname === "/mercado-da-bola";

  useEffect(() => {
    if (shouldHide) return;

    async function loadBreakingNews() {
      const [newsResult, settingsResult] = await Promise.all([
        supabase
          .from("news")
          .select("id,title,slug")
          .eq("status", "publicado")
          .or(publishedNowFilter())
          .eq("is_breaking", true)
          .order("published_at", { ascending: false }),
        supabase
          .from("site_settings")
          .select("value")
          .eq("key", "breaking_news")
          .maybeSingle(),
      ]);

      setItems((newsResult.data || []) as BreakingNewsItem[]);
      setSpeedSeconds(clampTickerSpeed(settingsResult.data?.value?.speedSeconds));
    }

    loadBreakingNews();

    const interval = window.setInterval(loadBreakingNews, 30000);

    return () => window.clearInterval(interval);
  }, [shouldHide]);

  if (shouldHide || items.length === 0) return null;

  const tickerItems = [...items, ...items];

  return (
    <>
      <div className="fixed left-0 right-0 top-16 md:top-20 z-40 bg-alvinegro-black text-white border-t border-white/10 border-b border-white/10 overflow-hidden">
        <div className="flex h-10 items-center">
          <div className="shrink-0 self-stretch bg-red-600 px-4 md:px-6 text-[11px] md:text-xs font-bold uppercase tracking-wider flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Breaking News
          </div>

          <div className="relative overflow-hidden flex-1">
            <div
              className="breaking-news-track flex items-center whitespace-nowrap"
              data-speed-seconds={speedSeconds}
              style={{ animationDuration: `${speedSeconds}s` }}
            >
              {tickerItems.map((item, index) => (
                <button
                  key={`${item.id}-${index}`}
                  type="button"
                  onClick={() => navigate(`/noticias/${item.slug}`)}
                  className="inline-flex items-center gap-4 px-4 text-xs md:text-sm font-semibold text-white hover:text-white/80 transition-colors cursor-pointer"
                >
                  <span>{item.title}</span>
                  <span className="text-red-600 text-lg leading-none">•</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <style>{`
          .breaking-news-track {
            width: max-content;
            animation-name: breakingNewsTicker;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
          }

          .breaking-news-track:hover {
            animation-play-state: paused;
          }

          @keyframes breakingNewsTicker {
            0% {
              transform: translateX(0);
            }

            100% {
              transform: translateX(-50%);
            }
          }
        `}</style>
      </div>

      <div className="h-10" aria-hidden="true" />
    </>
  );
}
